import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type AuthenticationResponseJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { authApi, type ManagedPasskeyResult, type TotpLoginResult } from './api';

export class PasskeyUnavailableError extends Error {
  public constructor() {
    super('Passkey authentication is unavailable');
    this.name = 'PasskeyUnavailableError';
  }
}

export class PasskeyCeremonyCancelledError extends Error {
  public constructor() {
    super('Passkey authentication was cancelled');
    this.name = 'PasskeyCeremonyCancelledError';
  }
}

export function supportsPasskeyLogin(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Exécute une connexion WebAuthn découvrable de bout en bout côté navigateur.
 * Les options viennent exclusivement du serveur et la réponse brute est renvoyée au serveur sans
 * interprétation cryptographique côté client.
 */
export async function loginWithPasskey(requestExtendedSession: boolean): Promise<TotpLoginResult> {
  if (!supportsPasskeyLogin()) throw new PasskeyUnavailableError();

  const challenge = await authApi.beginPasskeyLogin();

  let response: AuthenticationResponseJSON;
  try {
    response = await startAuthentication({ optionsJSON: challenge.options });
  } catch (error) {
    if (isNeutralCeremonyCancellation(error)) throw new PasskeyCeremonyCancelledError();
    throw error;
  }

  return authApi.completePasskeyLogin(challenge.challengeId, response, requestExtendedSession);
}

/**
 * Enrôle une nouvelle passkey depuis une session déjà authentifiée. Le navigateur ne fabrique ni
 * RP ID ni identité : toutes les options WebAuthn sont fournies par l'API Tosho.
 */
export async function registerPasskey(
  displayName: string,
  csrfToken: string,
): Promise<ManagedPasskeyResult> {
  if (!supportsPasskeyLogin()) throw new PasskeyUnavailableError();

  const challenge = await authApi.beginPasskeyRegistration(csrfToken);

  let response: RegistrationResponseJSON;
  try {
    response = await startRegistration({ optionsJSON: challenge.options });
  } catch (error) {
    if (isNeutralCeremonyCancellation(error)) throw new PasskeyCeremonyCancelledError();
    throw error;
  }

  return authApi.completePasskeyRegistration(
    challenge.challengeId,
    displayName,
    response,
    csrfToken,
  );
}

function isNeutralCeremonyCancellation(error: unknown): boolean {
  if (!(error instanceof WebAuthnError)) return false;
  return error.code === 'ERROR_CEREMONY_ABORTED' || error.name === 'NotAllowedError';
}
